// REPORT-UI-WEB (2026-05-18).
//
// User-facing "Пожаловаться" / "Report" modal. Submits a structured report to
// the Katysha bridge at https://api.katysha.app/api/app/reports.
//
// Auth posture: until SMS-AUTH-001 DEPLOY ships, the main webapp uses
// Tinode-native session auth and cannot present a katysha-stub Bearer. The
// backend endpoint accepts the request anonymously (reporter_user_id stays
// NULL) gated by per-IP rate-limit. No admin token is ever in browser source.
//
// IMPORTANT (canon — docs/architecture/katysha-identity-and-moderation.md):
//   * Submitting a report does NOT auto-punish the reported user.
//   * A moderator will review the report in the admin queue.
//   * No client-provided reporter_user_id is accepted by backend.

import React from 'react';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';

const REPORT_API_URL = 'https://api.katysha.app/api/app/reports';

const REPORT_TYPES = [
  'spam',
  'scam',
  'abuse',
  'harassment',
  'illegal_content',
  'violence_threat',
  'impersonation',
  'other',
];

const messages = defineMessages({
  title: {
    id: 'report_dialog_title',
    defaultMessage: 'Report message',
    description: 'Title of the report-message dialog',
  },
  intro: {
    id: 'report_dialog_intro',
    defaultMessage: 'Reports go to moderators. The user is not blocked automatically.',
    description: 'Explanation banner inside the report dialog',
  },
  type_label: {
    id: 'report_dialog_type_label',
    defaultMessage: 'Reason',
    description: 'Field label: type of report',
  },
  description_label: {
    id: 'report_dialog_description_label',
    defaultMessage: 'Comment (optional)',
    description: 'Field label: free-text description',
  },
  description_placeholder: {
    id: 'report_dialog_description_placeholder',
    defaultMessage: 'Briefly describe the problem (optional)',
    description: 'Placeholder for the description textarea',
  },
  submit: {
    id: 'report_dialog_submit',
    defaultMessage: 'Submit report',
    description: 'Button: submit the report',
  },
  cancel: {
    id: 'report_dialog_cancel',
    defaultMessage: 'Cancel',
    description: 'Button: close the report dialog without submitting',
  },
  success: {
    id: 'report_dialog_success',
    defaultMessage: 'Thank you. The report has been submitted to moderators.',
    description: 'Success message after submitting a report',
  },
  error_generic: {
    id: 'report_dialog_error_generic',
    defaultMessage: 'Could not submit the report. Please try again later.',
    description: 'Generic error message when report submission fails',
  },
  error_rate_limit: {
    id: 'report_dialog_error_rate_limit',
    defaultMessage: 'Too many reports from this device. Please wait a few minutes.',
    description: 'Error message when the per-IP report rate-limit is hit',
  },
  type_spam:            { id: 'report_dialog_type_spam',             defaultMessage: 'Spam',            description: 'Report reason: spam' },
  type_scam:            { id: 'report_dialog_type_scam',             defaultMessage: 'Scam / fraud',    description: 'Report reason: scam' },
  type_abuse:           { id: 'report_dialog_type_abuse',            defaultMessage: 'Abuse',           description: 'Report reason: abuse' },
  type_harassment:      { id: 'report_dialog_type_harassment',       defaultMessage: 'Harassment',      description: 'Report reason: harassment' },
  type_illegal_content: { id: 'report_dialog_type_illegal_content',  defaultMessage: 'Illegal content', description: 'Report reason: illegal content' },
  type_violence_threat: { id: 'report_dialog_type_violence_threat',  defaultMessage: 'Violence threat', description: 'Report reason: violence threat' },
  type_impersonation:   { id: 'report_dialog_type_impersonation',    defaultMessage: 'Impersonation',   description: 'Report reason: impersonation' },
  type_other:           { id: 'report_dialog_type_other',            defaultMessage: 'Other',           description: 'Report reason: other' },
});

class ReportMessageDialog extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      reportType:  'spam',
      description: '',
      submitting:  false,
      submitted:   false,
      error:       null,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClose  = this.handleClose.bind(this);
  }

  handleClose() {
    if (this.state.submitting) return;
    this.props.onHide();
  }

  async handleSubmit(e) {
    e.preventDefault();
    if (this.state.submitting) return;
    this.setState({ submitting: true, error: null });
    const params = this.props.params || {};
    const body = {
      tinode_topic_id:      params.topicName || null,
      tinode_message_seqid: params.seq != null ? Number(params.seq) : null,
      report_type:          this.state.reportType,
      description:          (this.state.description || '').trim() || null,
    };
    // Strip nulls so we don't send {tinode_topic_id:null}.
    Object.keys(body).forEach(k => { if (body[k] == null) delete body[k]; });
    try {
      const r = await fetch(REPORT_API_URL, {
        method:  'POST',
        mode:    'cors',
        credentials: 'omit',   // never attach Tinode session cookies/auth
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = data && data.detail;
        let kind = 'generic';
        if (typeof detail === 'string' && detail.indexOf('rate_limit') >= 0) kind = 'rate_limit';
        this.setState({ submitting: false, error: kind });
        return;
      }
      this.setState({ submitting: false, submitted: true });
    } catch (err) {
      this.setState({ submitting: false, error: 'generic' });
    }
  }

  render() {
    const { intl } = this.props;
    const { formatMessage } = intl;

    return (
      <div className="alert-container" onClick={this.handleClose}>
        <div className="alert-box report-dialog"
             onClick={(e) => e.stopPropagation()}
             style={{ maxWidth: 460 }}>
          <div className="alert-title">
            <FormattedMessage {...messages.title} />
          </div>
          <div className="alert-content">
            {this.state.submitted ? (
              <div className="report-success" style={{ color: '#2e7d32', padding: '8px 0' }}>
                <FormattedMessage {...messages.success} />
              </div>
            ) : (
              <form onSubmit={this.handleSubmit}>
                <div style={{ background: '#fffbeb', border: '1px solid #fcd34d',
                              borderRadius: 6, padding: '8px 10px', marginBottom: 12,
                              fontSize: 12, color: '#78350f' }}>
                  <FormattedMessage {...messages.intro} />
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label style={{ display:'block', fontSize: 12, color: '#666', marginBottom: 4 }}>
                    <FormattedMessage {...messages.type_label} />
                  </label>
                  <select
                    value={this.state.reportType}
                    onChange={(e) => this.setState({ reportType: e.target.value })}
                    disabled={this.state.submitting}
                    style={{ width: '100%', padding: '6px 8px', fontSize: 14 }}>
                    {REPORT_TYPES.map(rt => (
                      <option key={rt} value={rt}>
                        {formatMessage(messages['type_' + rt])}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label style={{ display:'block', fontSize: 12, color: '#666', marginBottom: 4 }}>
                    <FormattedMessage {...messages.description_label} />
                  </label>
                  <textarea
                    rows={3}
                    maxLength={2000}
                    placeholder={formatMessage(messages.description_placeholder)}
                    value={this.state.description}
                    onChange={(e) => this.setState({ description: e.target.value })}
                    disabled={this.state.submitting}
                    style={{ width: '100%', padding: '6px 8px', fontSize: 14, resize: 'vertical' }} />
                </div>

                {this.state.error && (
                  <div className="report-error"
                       style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>
                    <FormattedMessage {...(this.state.error === 'rate_limit'
                                            ? messages.error_rate_limit
                                            : messages.error_generic)} />
                  </div>
                )}
              </form>
            )}
          </div>
          <div className="alert-buttons">
            {this.state.submitted ? (
              <button className="blue" onClick={this.handleClose} autoFocus>
                <FormattedMessage id="dialog_ok" defaultMessage="OK"
                                  description="Generic OK button" />
              </button>
            ) : (
              <>
                <button className="gray" onClick={this.handleClose}
                        disabled={this.state.submitting}>
                  <FormattedMessage {...messages.cancel} />
                </button>
                <button className="blue" onClick={this.handleSubmit}
                        disabled={this.state.submitting}>
                  <FormattedMessage {...messages.submit} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(ReportMessageDialog);
